import axios from 'axios'
import { getCurrentToken, refreshToken } from './authentication'
import { store } from '../store'
import { clearUser } from '../actions/userActions'
import { type AuthToken } from '../types/AuthToken'

let isRefreshing = false
let isReloading = false
let refreshSubscribers: ((token: AuthToken) => void)[] = []

export const API_URL = `${process.env.KLUBBLE_API_BASE_PATH}/api/`

const subscribeTokenRefresh = (callback: (token: AuthToken) => void) => {
  refreshSubscribers.push(callback)
}

const onRefreshed = (token: AuthToken) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

const createKlubbleAPI = () => {
  const axiosInstance = axios.create({ baseURL: API_URL })
  axiosInstance.defaults.headers.common['client-version'] = process.env.KLUBBLE_VERSION

  const authToken = getCurrentToken()
  if (authToken) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${authToken.jwtToken}`
  }

  axiosInstance.interceptors.response.use(
    (response) => {
      const serverVersion = response.headers['server-version']
      if (process.env.KLUBBLE_RELOAD_ON_VERSION_MISMATCH === 'true' && !isReloading && serverVersion && serverVersion !== process.env.KLUBBLE_VERSION) {
        // eslint-disable-next-line no-console
        console.log(`Forwarding because server version is ${serverVersion} and client ${process.env.KLUBBLE_VERSION}...`)

        // hard refresh because we deteced a difference between versions
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('version', serverVersion)
        isReloading = true
        window.location.search = searchParams.toString()
      }
      return response
    },
    (error) => {
      const originalRequest = error.config
      const currentToken = getCurrentToken()

      if (!originalRequest.retry && currentToken && (error.response?.status === 401 || error.response?.status === 403)) {
        const retryOrigReq = new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            if (token) {
              originalRequest.retry = true
              originalRequest.headers.Authorization = `Bearer ${token.jwtToken}`
              resolve(axiosInstance.request(originalRequest))
            } else {
              reject(error)
            }
          })
        })

        if (!isRefreshing) {
          isRefreshing = true
          refreshToken().then((newToken) => {
            onRefreshed(newToken)
          }).catch(() => {
            refreshSubscribers = []
          }).finally(() => {
            isRefreshing = false
          })
        }

        return retryOrigReq
      }

      if (!currentToken) {
        clearUser()(store.dispatch)
      }

      return Promise.reject(error)
    },
  )

  return axiosInstance
}

export const klubbleAPI = createKlubbleAPI()

export interface ApiResult<T> {
  success: boolean
  message: string
  data: T
}

export type ApiFormResult<TSuccessResponse, TFormInputs> = ApiResult<TSuccessResponse> & {
  errors: { [key in keyof TFormInputs]: string }
}
